import { useRef, useState, useEffect, RefObject } from 'react';

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

interface UseScrollRevealReturn<T extends HTMLElement> {
  ref: RefObject<T>;
  isVisible: boolean;
  hasTriggered: boolean;
}

export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollRevealOptions = {}
): UseScrollRevealReturn<T> {
  const { threshold = 0.1, rootMargin = '0px', triggerOnce = true } = options;

  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // If already triggered and triggerOnce is true, skip
    if (hasTriggered && triggerOnce) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        setIsVisible(visible);

        if (visible && !hasTriggered) {
          setHasTriggered(true);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce, hasTriggered]);

  return { ref, isVisible: triggerOnce ? hasTriggered : isVisible, hasTriggered };
}

// Stagger animation helper
export function useStaggeredReveal(
  itemCount: number,
  options: UseScrollRevealOptions & { staggerDelay?: number } = {}
) {
  const { staggerDelay = 100, ...revealOptions } = options;
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(revealOptions);

  const getItemDelay = (index: number) => ({
    transitionDelay: `${index * staggerDelay}ms`,
  });

  const getItemClasses = (index: number, baseClass: string = '') => {
    const visibleClass = isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8';
    return `${baseClass} transition-all duration-500 ease-out ${visibleClass}`;
  };

  return { ref, isVisible, getItemDelay, getItemClasses };
}

export default useScrollReveal;
